/**
 * Список бесед — топовый современный дизайн
 * Дата: 30.01.2026 — REDESIGN: вау-эффект без тяжелых анимаций
 * Дата: 05.02.2026 — Добавлены аватары пациентов из WhatsApp (TODO-055)
 * 
 * СВЯЗАННЫЕ МОДУЛИ:
 * - Messages.tsx - родительская страница
 * - ConversationDTO из api/types.ts - структура беседы (avatar_url)
 * 
 * 24.10.2026 — строка 76px: имя и превью крупнее, канал текстом.
 * Не удалять по требованию владельца системы.
 * CROSS-REF: pages/Messages.tsx, ChatWindow.tsx
 */
import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { contactModeBadgeLabel } from '@/lib/contactModePresentation';
import { ConversationDTO } from '@/api/types';
import { User } from 'lucide-react';

const isMessengerID = (phone: string | undefined | null): boolean => {
  if (!phone) return false;
  return phone.startsWith('tg:') || phone.startsWith('max:');
};

const isCanonicalPhone = (value: string | undefined | null): boolean => {
  if (!value) return false;
  return /^7\d{10}$/.test(value);
};

const getConversationThreadKey = (conv: ConversationDTO): string => {
  const raw = conv.phone_normalized || conv.phone || '';
  if (!raw || isMessengerID(raw) || isCanonicalPhone(raw)) {
    return raw;
  }

  if (conv.messenger?.includes('telegram')) {
    return `tg:${raw}`;
  }
  if (conv.messenger === 'max') {
    return `max:${raw}`;
  }
  if (conv.messenger === 'max_client') {
    return raw.startsWith('max:') ? raw : `max:${raw}`;
  }

  return raw;
};

const formatMessengerID = (phone: string | undefined | null): string => {
  if (!phone) return '';
  if (phone.startsWith('tg:')) return `TG: ${phone.substring(3)}`;
  if (phone.startsWith('max:')) return `MAX: ${phone.substring(4)}`;
  return phone;
};

const getConversationDisplayName = (conv: ConversationDTO): string => {
  const threadKey = getConversationThreadKey(conv);
  const messengerFallback =
    (isMessengerID(threadKey) ? formatMessengerID(threadKey) : conv.phone) || 'Неизвестный';

  if (conv.chat_type === 'group') {
    return conv.chat_title || messengerFallback || conv.patient_name || 'Неизвестный';
  }

  return conv.patient_name || conv.chat_title || messengerFallback || 'Неизвестный';
};

interface Props {
  conversations: ConversationDTO[];
  selectedPhone: string | null;
  onSelect: (phone: string, patientName?: string) => void;
  onMarkProcessed?: (phone: string) => void;
}

// 30.01.2026 - Badge конфиг с градиентами
const channelConfig: Record<string, { label: string; gradient: string; textColor: string }> = {
  whatsapp_waba: { label: 'WABA', gradient: 'from-emerald-500 to-emerald-600', textColor: 'text-white' },
  whatsapp_regular: { label: 'Reg', gradient: 'from-green-400 to-green-500', textColor: 'text-white' },
  whatsapp: { label: 'WA', gradient: 'from-green-400 to-green-500', textColor: 'text-white' },
  telegram_bot: { label: 'TG Bot', gradient: 'from-blue-400 to-blue-500', textColor: 'text-white' },
  telegram_client: { label: 'TG Cli', gradient: 'from-sky-400 to-sky-500', textColor: 'text-white' },
  telegram: { label: 'TG', gradient: 'from-blue-400 to-blue-500', textColor: 'text-white' },
  max: { label: 'MAX', gradient: 'from-purple-400 to-purple-500', textColor: 'text-white' },
  max_client: { label: 'Max Cli', gradient: 'from-fuchsia-400 to-fuchsia-500', textColor: 'text-white' },
  sms: { label: 'SMS', gradient: 'from-amber-400 to-amber-500', textColor: 'text-white' },
};

const channelLabel = (messenger: string, messengerType?: string): string => {
  const key = messengerType === 'waba' ? 'whatsapp_waba'
    : messengerType === 'regular' ? 'whatsapp_regular'
    : messenger;
  return channelConfig[key]?.label || channelConfig.whatsapp.label;
};

function conversationAvatarSrc(raw?: string): string | undefined {
  const url = (raw || '').trim();
  if (!url) return undefined;
  if (url.startsWith('/api/media/proxy')) return url;
  if (/wazzup/i.test(url)) {
    return `/api/media/proxy?url=${encodeURIComponent(url)}&thumbnail=1`;
  }
  return url;
}

// 08.09.2026 — фото или иконка человека (без инициалов)
function AvatarWithFallback({
  avatarUrl,
  patientName,
}: {
  avatarUrl?: string;
  patientName?: string;
}) {
  const [imageError, setImageError] = useState(false);
  const proxied = conversationAvatarSrc(avatarUrl);
  const showImage = Boolean(proxied) && !imageError;

  useEffect(() => {
    setImageError(false);
  }, [proxied]);

  return (
    <div className={cn(
      'flex h-10 w-10 items-center justify-center overflow-hidden rounded-full',
      !showImage && 'bg-shell-primary-soft text-shell-primary-strong',
    )}>
      {showImage ? (
        <img 
          src={proxied}
          alt={patientName || 'Аватар'}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <User className="h-5 w-5" aria-hidden />
      )}
    </div>
  );
}

export function ConversationList({ conversations, selectedPhone, onSelect, onMarkProcessed }: Props) {
  const [processedMenu, setProcessedMenu] = useState<{ x: number; y: number; phone: string } | null>(null);
  return (
    <div>
      {conversations.map((conv) => {
        const phoneKey = getConversationThreadKey(conv);
        const displayName = getConversationDisplayName(conv);
        const isSelected = selectedPhone === phoneKey;
        const hasUnread = conv.unread_count > 0;
        const contactModeLabel = contactModeBadgeLabel(conv.contact_mode);
        const preview = [
          channelLabel(conv.messenger, conv.messenger_type),
          conv.chat_type === 'group' ? 'группа' : '',
          isMessengerID(phoneKey) && conv.chat_type !== 'group' ? 'телефон неизвестен' : '',
          contactModeLabel || '',
          conv.last_message || '',
        ].filter(Boolean).join(' · ');

        return (
          <button
            key={phoneKey}
            onClick={() => onSelect(phoneKey, displayName)}
            onContextMenu={(event) => {
              if (!hasUnread || !onMarkProcessed) return;
              event.preventDefault();
              setProcessedMenu({ x: event.clientX, y: event.clientY, phone: phoneKey });
            }}
            data-testid={`row-conversation-${phoneKey}`}
            className={cn(
              'flex min-h-[76px] w-full items-center gap-3 border-b border-shell-border px-3 py-2 text-left transition-colors duration-100',
              isSelected ? 'bg-shell-primary-soft shadow-[inset_3px_0_0_var(--shell-primary)]' : 'hover:bg-shell-hover',
            )}
          >
            <AvatarWithFallback
              avatarUrl={conv.avatar_url}
              patientName={displayName}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className={cn('truncate text-sm font-semibold', hasUnread ? 'text-shell-strong' : 'text-shell-text')}>
                  {displayName}
                </p>
                {conv.last_message_at && (
                  <span className="shrink-0 text-[11px] text-shell-muted">{formatMessageTime(conv.last_message_at)}</span>
                )}
              </div>
              <p className={cn('mt-0.5 truncate text-[13px] leading-5', hasUnread ? 'text-shell-text' : 'text-shell-muted')}>
                {preview}
              </p>
            </div>
            {hasUnread && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-shell-primary-strong px-1.5 text-[11px] font-semibold text-white">
                {conv.unread_count > 99 ? '99+' : conv.unread_count}
              </span>
            )}
          </button>
        );
      })}
      {processedMenu ? (
        <>
          <button
            type="button"
            aria-label="Закрыть меню"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setProcessedMenu(null)}
            onContextMenu={(event) => {
              event.preventDefault();
              setProcessedMenu(null);
            }}
          />
          <div
            role="menu"
            className="fixed z-50 min-w-[160px] rounded-lg border border-neutral-200 bg-white py-1 text-sm shadow-lg"
            style={{ left: processedMenu.x, top: processedMenu.y }}
          >
            <button
              type="button"
              role="menuitem"
              className="w-full px-3 py-2 text-left font-semibold text-neutral-900 hover:bg-neutral-100"
              onClick={() => {
                const phone = processedMenu.phone;
                setProcessedMenu(null);
                onMarkProcessed?.(phone);
              }}
            >
              Обработано
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function formatMessageTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) {
    return 'Вчера';
  }
  if (diffDays < 7) {
    return date.toLocaleDateString('ru-RU', { weekday: 'short' });
  }
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}
